// frontend/src/pages/OfferForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function OfferForm() {
  const { id } = useParams();               // "new" | "<id numerico>"
  const isNew = !id || id === "new";
  const nav = useNavigate();

  // form state
  const [active, setActive] = useState(true);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [oldPrice, setOldPrice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);

  // image
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(""); // dall’API
  const imagePreview = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    return imageUrl || "";
  }, [imageFile, imageUrl]);

  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/prodotti/?tipo_listino=cliente`);
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data) ? data : (data.results || data.prodotti || []);
        setProducts(list);
      } catch (e) {
        console.error("Errore caricamento prodotti", e);
      }
    })();
  }, []);

  // Carica in edit
  useEffect(() => {
    if (isNew) {
      setLoaded(true);
      return;
    }
    (async () => {
      setError("");
      try {
        const res = await authFetch(`${API_URL}/api/offers/${id}/`);
        if (!res.ok) throw new Error(await res.text());
        const o = await res.json();
        setActive(!!o.active);
        setTitle(o.title || "");
        setSubtitle(o.subtitle || "");
        setDescription(o.description || "");
        setPrice(o.price != null ? String(o.price) : "");
        setOldPrice(o.old_price != null ? String(o.old_price) : "");
        setStartDate(o.start_date || "");
        setEndDate(o.end_date || "");
        setImageUrl(o.image_url || "");
          setProductId(o.product ? String(o.product) : "");
      } catch (e) {
        console.error(e);
        setError("Impossibile caricare l’offerta.");
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (f) setImageFile(f);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
        if (!productId) {
          setError("Seleziona il prodotto collegato: serve per rendere l'offerta acquistabile dall'app.");
          setSaving(false);
          return;
        }

      const fd = new FormData();
      // inviamo solo i campi valorizzati (server accetta partial)
      fd.append("active", active ? "true" : "false");
      if (title) fd.append("title", title);
      if (subtitle) fd.append("subtitle", subtitle);
      if (description) fd.append("description", description);
      if (price !== "") fd.append("price", price);
      if (oldPrice !== "") fd.append("old_price", oldPrice);
      if (startDate) fd.append("start_date", startDate);
      if (endDate) fd.append("end_date", endDate);
      if (imageFile) fd.append("image", imageFile);
        fd.append("product", productId);

      const url = isNew ? `${API_URL}/api/offers/` : `${API_URL}/api/offers/${id}/`;
      const method = isNew ? "POST" : "PATCH";
      const res = await authFetch(url, { method, body: fd }); // authFetch NON setta Content-Type per FormData
      if (!res.ok) throw new Error(await res.text());

      // torna alla lista
      nav("/offers");
    } catch (e) {
      console.error(e);
      setError("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="container mt-4"><p>Caricamento…</p></div>;

  return (
    <div className="container mt-4">
      {/* Header con ← Indietro e titolo coerente */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link to="/offers" className="btn btn-outline-secondary">← Indietro</Link>
        <h3 className="m-0">{isNew ? "➕ Nuova Offerta" : "✏️ Modifica Offerta"}</h3>
        <div style={{ width: 120 }} />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3">
        <div className="col-12 d-flex align-items-center gap-2">
          <label className="form-check-label">
            <input
              type="checkbox"
              className="form-check-input me-2"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Attiva
          </label>
        </div>

        <div className="col-12">
          <label className="form-label">Titolo</label>
          <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className="col-12">
          <label className="form-label">Sottotitolo</label>
          <input className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </div>

        <div className="col-12 col-lg-8">
          <label className="form-label">Descrizione</label>
          <textarea
            className="form-control"
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="col-12 col-lg-4">
          <label className="form-label">Immagine</label>
          <div className="d-flex align-items-center gap-2">
            <input type="file" className="form-control" onChange={onPickImage} />
          </div>
          {imagePreview ? (
            <div className="mt-2">
              <img
                src={imagePreview}
                alt="preview"
                style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8 }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </div>
          ) : (
            <div className="text-muted small mt-2">Nessuna immagine</div>
          )}
        </div>

          <div className="col-12">
            <label className="form-label">Prodotto collegato per ordine app *</label>
            <select
              className="form-select"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
            >
              <option value="">Seleziona prodotto reale...</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.title || `Prodotto #${p.id}`} {p.category ? `— ${p.category}` : ""}
                </option>
              ))}
            </select>
            <div className="form-text">
              Obbligatorio: l'app usa questo prodotto per creare l'ordine, mentre il prezzo mostrato resta quello dell'offerta.
            </div>
          </div>

        <div className="col-12 col-md-6 col-lg-3">
          <label className="form-label">Prezzo</label>
          <input
            className="form-control"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="es. 29.90"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <label className="form-label">Prezzo barrato</label>
          <input
            className="form-control"
            inputMode="decimal"
            value={oldPrice}
            onChange={(e) => setOldPrice(e.target.value)}
            placeholder="es. 39.90"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <label className="form-label">Dal</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <label className="form-label">Al</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="col-12 d-flex gap-2 mt-2">
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? "Salvataggio…" : "Salva"}
          </button>
          <Link to="/offers" className="btn btn-outline-secondary">Annulla</Link>
        </div>
      </div>
    </div>
  );
}
